import type { ResponseBody } from "../openapi/helpers";
import type { paths } from "../openapi/schema";
import {
  NOTIFICATION_TYPE_REGISTRY,
  type SupportedNotificationType,
} from "./notification-contract";

declare const __DEV__: boolean | undefined;

/** Keep fixtures aligned with the API response contract used by the app. */
export type NotificationFixture = ResponseBody<
  paths["/{tenantId}/me/notifications"]["get"]
>["data"][number];

export type NotificationFixtureMode =
  "all" | "badges" | "empty" | "read" | "stale" | "error" | "loading";

const FIXTURE_USER_ID = "00000000-0000-4000-8000-000000000172";
const FIXTURE_BASE_TIME = "2026-07-17T12:00:00.000Z";
const FIXTURE_EXPIRED_TIME = "2024-01-01T00:00:00.000Z";

type FixtureSeed = Pick<
  NotificationFixture,
  "body" | "data" | "readAt" | "reminderId" | "fireAtUtc" | "expiresAt"
> & {
  id: string;
  createdAt: string;
  updatedAt: string;
};

function minutesAgo(minutes: number) {
  return new Date(
    Date.parse(FIXTURE_BASE_TIME) - minutes * 60 * 1000,
  ).toISOString();
}

function makeFixture(
  type: SupportedNotificationType,
  seed: Omit<FixtureSeed, "id" | "type" | "createdAt" | "updatedAt"> & {
    id: string;
    title: string;
    createdMinutesAgo: number;
    type?: SupportedNotificationType;
  },
): NotificationFixture {
  const createdAt = minutesAgo(seed.createdMinutesAgo);
  return {
    id: seed.id,
    userId: FIXTURE_USER_ID,
    channel: "web",
    type: seed.type ?? type,
    title: seed.title,
    body: seed.body,
    imageUrl: null,
    data: seed.data,
    idempotencyKey: null,
    readAt: seed.readAt,
    reminderId: seed.reminderId,
    fireAtUtc: seed.fireAtUtc,
    createdAt,
    updatedAt: createdAt,
    updatedBy: null,
    expiresAt: seed.expiresAt,
  };
}

const SUPPORTED_FIXTURES: Record<
  SupportedNotificationType,
  NotificationFixture
> = {
  medication_reminder: makeFixture("medication_reminder", {
    id: "fixture-notification-medication",
    title: "Medication reminder",
    body: "It is time to review your medication schedule.",
    data: { linkType: "medication", linkId: "fixture-medication" },
    readAt: null,
    reminderId: "00000000-0000-4000-8000-000000000201",
    fireAtUtc: minutesAgo(-15),
    expiresAt: null,
    createdMinutesAgo: 4,
  }),
  appointment_reminder: makeFixture("appointment_reminder", {
    id: "fixture-notification-appointment",
    title: "Upcoming appointment",
    body: "Your appointment is tomorrow at 9:00 AM.",
    data: { linkType: "appointment", linkId: "fixture-appointment" },
    readAt: null,
    reminderId: "00000000-0000-4000-8000-000000000202",
    fireAtUtc: minutesAgo(-60),
    expiresAt: null,
    createdMinutesAgo: 12,
  }),
  test_result_available: makeFixture("test_result_available", {
    id: "fixture-notification-test-result",
    title: "A test result is ready",
    body: "Open your Lab Results to review the latest update.",
    data: { linkType: "test_result", linkId: "fixture-test-result" },
    readAt: null,
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 22,
  }),
  document_request: makeFixture("document_request", {
    id: "fixture-notification-document-request",
    title: "Document requested",
    body: "Louise is waiting for a document from you.",
    data: { linkType: "document_request", linkId: "fixture-document-request" },
    readAt: minutesAgo(30),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 35,
  }),
  document_update: makeFixture("document_update", {
    id: "fixture-notification-document-update",
    title: "Document updated",
    body: "A document in your care record has changed.",
    data: { linkType: "document", linkId: "fixture-document" },
    readAt: minutesAgo(42),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 48,
  }),
  care_team_message: makeFixture("care_team_message", {
    id: "fixture-notification-care-team-message",
    title: "A message from your care team",
    body: "Your care team has a new update for you.",
    data: { linkType: "assistant", conversationId: "fixture-care-team" },
    readAt: minutesAgo(50),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 54,
  }),
  partner_invitation_sent: makeFixture("partner_invitation_sent", {
    id: "fixture-notification-partner-invitation-sent",
    title: "Alex invited you to share their journey",
    body: "Review the invitation in Connections.",
    data: {
      linkType: "partner",
      invitationId: "fixture-partner-invitation",
      inviterName: "Alex",
    },
    readAt: null,
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 1,
  }),
  partner_invitation_accepted: makeFixture("partner_invitation_accepted", {
    id: "fixture-notification-partner-invitation-accepted",
    title: "Jordan accepted your partner invitation",
    body: "You're now connected and can share your journey together",
    data: {
      linkType: "partner",
      invitationId: "fixture-partner-invitation-accepted",
      partnerPatientId: "fixture-partner-jordan",
      partnerName: "Jordan",
    },
    readAt: minutesAgo(62),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 2,
  }),
  partner_connected: makeFixture("partner_connected", {
    id: "fixture-notification-partner-connected",
    title: "You're connected with Alex",
    body: "Your partner connection is ready and sharing is active",
    data: {
      linkType: "partner",
      invitationId: "fixture-partner-connected",
      partnerPatientId: "fixture-partner-alex",
      partnerName: "Alex",
    },
    readAt: minutesAgo(70),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 3,
  }),
  clinic_update: makeFixture("clinic_update", {
    id: "fixture-notification-clinic",
    title: "Clinic update",
    body: "There is a new update from your care network.",
    data: { linkType: "clinic", linkId: "fixture-clinic" },
    readAt: minutesAgo(55),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 60,
  }),
  coverage_status: makeFixture("coverage_status", {
    id: "fixture-notification-coverage",
    title: "Coverage status changed",
    body: "Review the latest status of your coverage.",
    data: { linkType: "coverage" },
    readAt: minutesAgo(68),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 72,
  }),
  payment_status: makeFixture("payment_status", {
    id: "fixture-notification-payment",
    title: "Payment update",
    body: "A payment or reimbursement update is available.",
    data: { linkType: "payment" },
    readAt: minutesAgo(80),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 84,
  }),
  cycle_update: makeFixture("cycle_update", {
    id: "fixture-notification-cycle",
    title: "Cycle update",
    body: "Your care timeline has a new update.",
    data: { linkType: "cycle" },
    readAt: minutesAgo(95),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 96,
  }),
  period_prediction: makeFixture("period_prediction", {
    id: "fixture-notification-period",
    title: "Period prediction update",
    body: "Your cycle prediction is ready to review.",
    data: { linkType: "cycle" },
    readAt: minutesAgo(110),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 108,
  }),
  care_task: makeFixture("care_task", {
    id: "fixture-notification-care-task",
    title: "Care task to review",
    body: "There is a next step waiting in your care plan.",
    data: { linkType: "medical_history" },
    readAt: minutesAgo(125),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 120,
  }),
  system: makeFixture("system", {
    id: "fixture-notification-system",
    title: "Louise update",
    body: "Your Louise account has a new update.",
    data: null,
    readAt: minutesAgo(140),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 132,
  }),
  badge_unlocked: makeFixture("badge_unlocked", {
    id: "fixture-notification-badge",
    title: "Milestone unlocked",
    body: "You reached a new milestone.",
    data: {
      eventId: "fixture-badge-event",
      badgeId: "first-steps",
      criteriaVersion: "1",
      userId: FIXTURE_USER_ID,
      sourceDomainEvent: "fixture",
      occurredAt: FIXTURE_BASE_TIME,
      copy: {
        title: "Milestone unlocked",
        body: "You reached a new milestone.",
        milestone: "fixture",
        celebrationMode: "quiet",
      },
    },
    readAt: minutesAgo(155),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 144,
  }),
  cycle_insight: makeFixture("cycle_insight", {
    id: "fixture-notification-cycle-insight",
    title: "Cycle insight available",
    body: "There is a new insight in your care overview.",
    data: { linkType: "cycle" },
    readAt: minutesAgo(170),
    reminderId: null,
    fireAtUtc: null,
    expiresAt: null,
    createdMinutesAgo: 156,
  }),
};

const UNKNOWN_FIXTURE = makeFixture("system", {
  id: "fixture-notification-unknown",
  type: "notification_type_added_by_backend" as SupportedNotificationType,
  title: "New notification",
  body: "This row uses the safe fallback for an unknown notification type.",
  data: null,
  readAt: minutesAgo(185),
  reminderId: null,
  fireAtUtc: null,
  expiresAt: null,
  createdMinutesAgo: 168,
});

const STALE_LINK_FIXTURE = makeFixture("medication_reminder", {
  id: "fixture-notification-stale-link",
  title: "Medication reminder with stale link",
  body: "The medication is no longer available; open your medication list.",
  data: { linkType: "medication" },
  readAt: minutesAgo(200),
  reminderId: null,
  fireAtUtc: null,
  expiresAt: null,
  createdMinutesAgo: 180,
});

const EXPIRED_FIXTURE = makeFixture("system", {
  id: "fixture-notification-expired",
  title: "Expired notification",
  body: "This row must not appear in the feed.",
  data: null,
  readAt: null,
  reminderId: null,
  fireAtUtc: null,
  expiresAt: FIXTURE_EXPIRED_TIME,
  createdMinutesAgo: 200,
});

function cloneNotification(
  notification: NotificationFixture,
): NotificationFixture {
  return {
    ...notification,
    data:
      notification.data && typeof notification.data === "object"
        ? { ...(notification.data as Record<string, unknown>) }
        : notification.data,
  };
}

function activeNotifications(notifications: NotificationFixture[]) {
  return notifications.filter((notification) => {
    if (!notification.expiresAt) return true;
    const expiresAt = Date.parse(notification.expiresAt);
    return Number.isNaN(expiresAt) || expiresAt > Date.now();
  });
}

function sortNotificationsNewestFirst(notifications: NotificationFixture[]) {
  return [...notifications].sort((a, b) => {
    const byDate = Date.parse(b.createdAt) - Date.parse(a.createdAt);
    if (byDate !== 0) return byDate;
    return b.id.localeCompare(a.id);
  });
}

/**
 * Several badge unlocks, unread, so the celebration is reachable at all.
 *
 * The single `badge_unlocked` fixture is already read, and the celebration
 * provider skips anything acknowledged — so no mode reached the dialog, and the
 * multi-badge carousel could not be seen without earning two badges against a
 * live API. These are the shape the server sends, with ids this build has
 * artwork for.
 */
function unreadBadgeUnlocks(): NotificationFixture[] {
  const unlocks: { badgeId: string; title: string; body: string }[] = [
    {
      badgeId: "first-steps",
      title: "First steps",
      body: "You started your record.",
    },
    {
      badgeId: "photo-day",
      title: "Photo day",
      body: "You added a photo to your profile.",
    },
    {
      badgeId: "clinic-finder",
      title: "Clinic finder",
      body: "You added your clinic.",
    },
  ];

  return unlocks.map((unlock, index) =>
    makeFixture("badge_unlocked", {
      id: `fixture-notification-badge-${unlock.badgeId}`,
      title: unlock.title,
      body: unlock.body,
      data: {
        eventId: `fixture-badge-event-${unlock.badgeId}`,
        badgeId: unlock.badgeId,
        criteriaVersion: "1",
        userId: FIXTURE_USER_ID,
        sourceDomainEvent: "fixture",
        occurredAt: FIXTURE_BASE_TIME,
        copy: {
          title: unlock.title,
          body: unlock.body,
          milestone: unlock.badgeId,
          celebrationMode: "quiet",
        },
      },
      readAt: null,
      reminderId: null,
      fireAtUtc: null,
      expiresAt: null,
      createdMinutesAgo: 10 + index,
    }),
  );
}

/** Create a fresh fixture set for Playwright/API mocks. */
export function createNotificationFixtureSet(
  mode: Exclude<NotificationFixtureMode, "error" | "loading"> = "all",
): NotificationFixture[] {
  if (mode === "empty") return [];

  const supported = Object.values(SUPPORTED_FIXTURES).map(cloneNotification);
  if (mode === "read") {
    return activeNotifications(
      supported.map((notification) => ({
        ...notification,
        readAt: notification.readAt ?? FIXTURE_BASE_TIME,
      })),
    );
  }
  if (mode === "stale") return [cloneNotification(STALE_LINK_FIXTURE)];
  if (mode === "badges") return unreadBadgeUnlocks();

  return [
    ...supported,
    cloneNotification(UNKNOWN_FIXTURE),
    cloneNotification(STALE_LINK_FIXTURE),
    // Keep the expired row in the backend-shaped fixture response so the feed
    // filtering path is exercised. The app removes it before rendering.
    cloneNotification(EXPIRED_FIXTURE),
  ];
}

/** Supported type registry and fixtures must stay in lockstep. */
export const NOTIFICATION_FIXTURE_TYPES = NOTIFICATION_TYPE_REGISTRY.map(
  ({ type }) => type,
);

export function getNotificationFixtureMode(): NotificationFixtureMode | null {
  if (
    !(__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") ||
    typeof window === "undefined"
  ) {
    return null;
  }
  const value = new URLSearchParams(window.location.search).get(
    "dev_notifications",
  );
  if (!value) return null;
  if (
    value === "all" ||
    value === "badges" ||
    value === "empty" ||
    value === "read" ||
    value === "stale" ||
    value === "error" ||
    value === "loading"
  ) {
    return value;
  }
  return "all";
}

const devFixtureStores = new Map<string, NotificationFixture[]>();

function getDevFixtureStore(
  mode: Exclude<NotificationFixtureMode, "error" | "loading">,
): NotificationFixture[] {
  let store = devFixtureStores.get(mode);
  if (!store) {
    store = createNotificationFixtureSet(mode);
    devFixtureStores.set(mode, store);
  }
  return store;
}

export async function fetchDevNotificationFixtures(
  mode: NotificationFixtureMode,
): Promise<NotificationFixture[]> {
  if (mode === "error") {
    throw new Error("Notification fixture error");
  }
  if (mode === "loading") {
    return new Promise<NotificationFixture[]>(() => {});
  }
  return sortNotificationsNewestFirst(
    activeNotifications(getDevFixtureStore(mode).map(cloneNotification)),
  );
}

export function getDevUnreadNotificationCount(
  mode: NotificationFixtureMode,
): number {
  if (mode === "error" || mode === "loading") return 0;
  return getDevFixtureStore(mode).filter(
    (notification) =>
      !notification.readAt && activeNotifications([notification]).length > 0,
  ).length;
}

export function markDevNotificationRead(
  mode: NotificationFixtureMode,
  notificationId: string,
) {
  if (mode === "error" || mode === "loading") return null;
  const store = getDevFixtureStore(mode);
  const notification = store.find(({ id }) => id === notificationId);
  if (!notification) return null;
  notification.readAt ??= FIXTURE_BASE_TIME;
  notification.updatedAt = FIXTURE_BASE_TIME;
  return cloneNotification(notification);
}

export function markAllDevNotificationsRead(mode: NotificationFixtureMode) {
  if (mode === "error" || mode === "loading") return 0;
  const store = getDevFixtureStore(mode);
  let count = 0;
  for (const notification of store) {
    if (
      !notification.readAt &&
      activeNotifications([notification]).length > 0
    ) {
      notification.readAt = FIXTURE_BASE_TIME;
      notification.updatedAt = FIXTURE_BASE_TIME;
      count += 1;
    }
  }
  return count;
}
